import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  CreditCard, TrendingUp, Users, DollarSign, Calendar, Loader2, 
  ArrowUpRight, ArrowDownRight, Download
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { getPracticeId } from '../../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBilling = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 12450,
    monthlyRevenue: 3200,
    activeMemberships: 186,
    avgMembershipValue: 149,
    revenueGrowth: 12.5,
    membershipGrowth: 8.3
  });
  const [recentTransactions, setRecentTransactions] = useState([]);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      // Simulate API call - in production, fetch real data
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mock recent transactions
      setRecentTransactions([
        { id: 1, client: 'Sarah Johnson', type: 'Membership', plan: 'Elevate', amount: 199, date: '2026-03-03', status: 'completed' },
        { id: 2, client: 'Michael Chen', type: 'Package', plan: 'Summer Glow', amount: 499, date: '2026-03-02', status: 'completed' },
        { id: 3, client: 'Emily Davis', type: 'Membership', plan: 'Embrace', amount: 99, date: '2026-03-02', status: 'completed' },
        { id: 4, client: 'James Wilson', type: 'Treatment', plan: 'HydraFacial', amount: 185, date: '2026-03-01', status: 'completed' },
        { id: 5, client: 'Lisa Anderson', type: 'Membership', plan: 'Elite', amount: 299, date: '2026-03-01', status: 'completed' },
      ]);
    } catch (error) {
      console.error('Error fetching billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    toast({
      title: 'Export Started',
      description: 'Your billing report is being generated...'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="admin-billing">
        <Loader2 className="h-8 w-8 animate-spin text-rose-500" />
        <span className="ml-2">Loading billing data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-billing">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Revenue & Billing</h1>
          <p className="text-gray-600 mt-1">Track your spa's revenue and client transactions</p>
        </div>
        <Button onClick={handleExport} variant="outline">
          <Download className="mr-2 h-4 w-4" />
          Export Report
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                <p className="text-3xl font-bold text-gray-900">${stats.totalRevenue.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-green-600 font-medium">{stats.revenueGrowth}%</span>
              <span className="text-gray-500 ml-1">vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Monthly Revenue</p>
                <p className="text-3xl font-bold text-gray-900">${stats.monthlyRevenue.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-gray-500">March 2026</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Active Memberships</p>
                <p className="text-3xl font-bold text-gray-900">{stats.activeMemberships}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-green-600 font-medium">{stats.membershipGrowth}%</span>
              <span className="text-gray-500 ml-1">growth</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Avg. Membership Value</p>
                <p className="text-3xl font-bold text-gray-900">${stats.avgMembershipValue}</p>
              </div>
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-rose-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-gray-500">Per month</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue by Source */}
        <Card>
          <CardHeader>
            <CardTitle>Revenue by Source</CardTitle>
            <CardDescription>Breakdown of revenue streams</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Memberships</span>
                  <span className="font-medium">$8,200 (65%)</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-rose-500 rounded-full" style={{ width: '65%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Packages</span>
                  <span className="font-medium">$2,800 (22%)</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: '22%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Individual Treatments</span>
                  <span className="font-medium">$1,100 (9%)</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500 rounded-full" style={{ width: '9%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Products</span>
                  <span className="font-medium">$350 (4%)</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: '4%' }} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Membership Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Membership Breakdown</CardTitle>
            <CardDescription>Active members by plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">Elevate</p>
                    <p className="text-sm text-gray-500">$199/month</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">89</p>
                  <p className="text-sm text-gray-500">members</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">Embrace</p>
                    <p className="text-sm text-gray-500">$99/month</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">67</p>
                  <p className="text-sm text-gray-500">members</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-purple-500 rounded-full mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">Elite</p>
                    <p className="text-sm text-gray-500">$299/month</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">30</p>
                  <p className="text-sm text-gray-500">members</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
          <CardDescription>Latest client payments and purchases</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Client</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Plan/Service</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.map((transaction) => (
                  <tr key={transaction.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{transaction.client}</td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className="text-xs">
                        {transaction.type}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{transaction.plan}</td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">${transaction.amount}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{transaction.date}</td>
                    <td className="py-3 px-4">
                      <Badge className="bg-green-100 text-green-800 text-xs">
                        {transaction.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBilling;
